import clsx from 'clsx';
import * as React from 'react';
import { useRecoilState } from 'recoil';

import type { RevisionPageDocument } from '@gitbook/hive-core';
import {
    AnimatedStateContext,
    LoadingPanel,
    Overlay,
    OverlayRoot,
    Portal,
    Skeleton,
    TextSkeleton,
    useScreenType,
} from '@gitbook/spine-react';
import { useLiveRef, usePrevious } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { FilesManager, ReusableContentsManager } from '../../../Components';
import { isTableOfContentOpenedOnMobileAtom } from '../../../state';
import { PagesToC } from './PagesToC';
import { TableOfContentsTabState, TableOfContentsTabStateProvider } from './state';
import { TableOfContentsHeader } from './TableOfContentsHeader';
import type { TableOfContentsProps } from './types';

/**
 * Table of contents for a space.
 */
export const TableOfContents = React.memo(function TableOfContents(props: TableOfContentsProps) {
    const { readOnly } = props;
    const { isDesktop } = useScreenType();

    const toc = (
        <TableOfContentsTabStateProvider readOnly={readOnly}>
            <TableOfContentsInner {...props} />
        </TableOfContentsTabStateProvider>
    );

    if (isDesktop) {
        return toc;
    }

    return <CollapsibleTableOfContents {...props}>{toc}</CollapsibleTableOfContents>;
});

function TableOfContentsInner(props: TableOfContentsProps) {
    const { space, changeRequest, analyticsContext, readOnly, setShowToCToggleButton } = props;

    const onHover = setShowToCToggleButton
        ? () => setShowToCToggleButton((prev) => !prev)
        : undefined;

    const { tab } = React.useContext(TableOfContentsTabState);

    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: just used for visual hover effect
        <div
            className="relative flex w-[--toc-desktop-width] flex-col justify-start border-r bg-base"
            onMouseEnter={onHover}
            onMouseLeave={onHover}
        >
            <TableOfContentsHeader readOnly={readOnly} analyticsContext={analyticsContext} />
            <div className="flex flex-1 flex-col overflow-y-auto">
                <React.Suspense fallback={<LoadingPanel size="large" />}>
                    {(() => {
                        switch (tab) {
                            case 'pages': {
                                return <PagesToC {...props} />;
                            }
                            case 'files':
                                return (
                                    <FilesManager
                                        space={space}
                                        changeRequest={changeRequest ?? null}
                                        readOnly={readOnly}
                                        className="pt-3 pr-3 pb-4 pl-6"
                                    />
                                );
                            case 'reusable-content':
                                return (
                                    <ReusableContentsManager
                                        space={space}
                                        className="pt-3 pr-3 pb-4 pl-6"
                                    />
                                );
                            default:
                                assertNever(tab);
                        }
                    })()}
                </React.Suspense>
            </div>
        </div>
    );
}

function CollapsibleTableOfContents(
    props: {
        children: React.ReactNode;
    } & TableOfContentsProps
) {
    const { children, currentPage } = props;

    const [isOpen, setIsOpen] = useRecoilState(isTableOfContentOpenedOnMobileAtom);

    // Close the ToC when changing page.
    useWatchPageChange(currentPage, () => {
        setIsOpen(false);
    });

    // Close the ToC when resizing the window.
    useOnWindowResize(() => {
        setIsOpen(false);
    });

    const [rect, watchPositionRef] = useWatchRect();

    return (
        <OverlayRoot isOpen={Boolean(isOpen && rect)} onOpenChange={setIsOpen}>
            {isOpen && <div ref={watchPositionRef} />}
            {rect && <TocOverlay rect={rect}>{children}</TocOverlay>}
        </OverlayRoot>
    );
}

/**
 * Watch window resize and call a callback when it happens.
 */
function useOnWindowResize(callback: () => void) {
    const callbackRef = useLiveRef(callback);
    React.useEffect(() => {
        const handleResize = () => callbackRef.current();
        window.addEventListener('resize', handleResize, { passive: true });
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [callbackRef]);
}

/**
 * Watch element rect and update it when it changes.
 */
function useWatchRect() {
    const [rect, setPosition] = React.useState<DOMRect | null>(null);
    const ref = React.useCallback((element: HTMLElement | null) => {
        if (element) {
            setPosition(element.getBoundingClientRect());
        }
    }, []);
    return [rect, ref] as const;
}

/**
 * Watch for page change and call a callback when it happens.
 */
function useWatchPageChange(currentPage: RevisionPageDocument | null, callback: () => void) {
    const callbackRef = useLiveRef(callback);
    const previousPage = usePrevious(currentPage);
    React.useEffect(() => {
        if (currentPage && previousPage && currentPage.id !== previousPage.id) {
            callbackRef.current();
        }
    }, [currentPage, previousPage, callbackRef]);
}

function TocOverlay(props: { children: React.ReactNode; rect: DOMRect }) {
    const animatedState = React.useContext(AnimatedStateContext);

    if (animatedState === 'exited') {
        return null;
    }

    const isExiting = animatedState === 'exiting';
    const isEntering = animatedState === 'entering';

    return (
        <Portal>
            <div className="fixed inset-0" />
            <Overlay
                className={clsx(
                    'fixed z-overlay flex overflow-auto fill-mode-forwards',
                    isEntering && 'fade-in slide-in-from-left-4 animate-in',
                    isExiting && 'fade-out slide-out-to-left-4 animate-out'
                )}
                style={{
                    top: props.rect.top,
                    left: props.rect.left,
                    bottom: 0,
                }}
            >
                {props.children}
            </Overlay>
        </Portal>
    );
}

function TableOfContentsContainer(props: React.ComponentPropsWithoutRef<'div'>) {
    return (
        <div
            {...props}
            className={clsx(
                'relative',
                'border-r bg-base',
                'flex flex-col justify-start',
                'w-[--toc-desktop-width]',
                props.className
            )}
        />
    );
}

/**
 * Skeleton for the table of contents.
 */
export function TableOfContentsSkeleton() {
    return (
        <TableOfContentsContainer className="gap-2 py-4 pl-6 max-lg:hidden">
            <PageItemSkeleton />
            <PageItemSkeleton />
            <PageItemSkeleton />
        </TableOfContentsContainer>
    );
}

function PageItemSkeleton() {
    return (
        <div className="flex items-center gap-2">
            <Skeleton shape="rect" className="size-6" />
            <TextSkeleton className="text-ui-heading-medium">Title of the page</TextSkeleton>
        </div>
    );
}
